// Free camera that stays on your side of the table.
// WASD = move, Q/E = down/up, mouse wheel = zoom, hold middle mouse = look around.
import * as THREE from 'three';
import { CONFIG } from '../../config.ts';

const HALF_PI = Math.PI / 2;

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  private pos = new THREE.Vector3();
  private yaw = 0; // 0 = facing the opponent
  private pitch = -0.7; // negative = looking down
  private keys = new Set<string>();
  private anim: { from: [THREE.Vector3, number, number]; to: [THREE.Vector3, number, number]; t: number } | null = null;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(CONFIG.camera.fov, aspect, 1, 3000);
    this.reset(false);
  }

  /** Back to the normal seated view. */
  reset(animate = true) {
    const s = CONFIG.camera.start;
    const pitch = Math.atan2(-s.y, s.z - s.lookAtZ);
    this.goTo(new THREE.Vector3(s.x, s.y, s.z), 0, pitch, animate);
  }

  /** Straight down over the whole table. You can move freely again afterwards. */
  topDown() {
    const t = CONFIG.camera.topDown;
    this.goTo(new THREE.Vector3(0, t.height, t.z), 0, -HALF_PI + 0.0001, true);
  }

  private goTo(pos: THREE.Vector3, yaw: number, pitch: number, animate: boolean) {
    if (!animate) {
      this.pos.copy(pos);
      this.yaw = yaw;
      this.pitch = pitch;
      this.anim = null;
      this.apply();
      return;
    }
    this.anim = { from: [this.pos.clone(), this.yaw, this.pitch], to: [pos.clone(), yaw, pitch], t: 0 };
  }

  setKey(code: string, down: boolean) {
    if (down) this.keys.add(code);
    else this.keys.delete(code);
  }
  clearKeys() {
    this.keys.clear();
  }

  look(dx: number, dy: number) {
    this.anim = null;
    this.yaw -= dx * CONFIG.camera.lookSpeed;
    this.pitch -= dy * CONFIG.camera.lookSpeed;
    this.clamp();
  }

  /** Drag the view across the table (left-drag on empty table). */
  pan(dx: number, dy: number) {
    this.anim = null;
    const scale = Math.max(0.05, this.pos.y / 700);
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this.pos.addScaledVector(right, -dx * scale);
    this.pos.addScaledVector(forward, dy * scale);
    this.clamp();
  }

  zoom(deltaY: number) {
    this.anim = null;
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    this.pos.addScaledVector(dir, -deltaY * 0.06);
    this.clamp();
  }

  private clamp() {
    const b = CONFIG.camera.bounds;
    this.pos.x = THREE.MathUtils.clamp(this.pos.x, -b.x, b.x);
    this.pos.y = THREE.MathUtils.clamp(this.pos.y, b.yMin, b.yMax);
    this.pos.z = THREE.MathUtils.clamp(this.pos.z, b.zMin, b.zMax);
    this.yaw = THREE.MathUtils.clamp(this.yaw, -1.4, 1.4);
    this.pitch = THREE.MathUtils.clamp(this.pitch, -HALF_PI + 0.0001, -0.03);
  }

  update(dt: number) {
    if (this.anim) {
      this.anim.t = Math.min(1, this.anim.t + dt / 0.55);
      const k = 1 - Math.pow(1 - this.anim.t, 3);
      const [p0, y0, pi0] = this.anim.from;
      const [p1, y1, pi1] = this.anim.to;
      this.pos.lerpVectors(p0, p1, k);
      this.yaw = y0 + (y1 - y0) * k;
      this.pitch = pi0 + (pi1 - pi0) * k;
      if (this.anim.t >= 1) this.anim = null;
    }
    const k = this.keys;
    const moving = ['KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE'].some((c) => k.has(c));
    if (moving) {
      this.anim = null;
      const speed = CONFIG.camera.moveSpeed * dt * (k.has('ShiftLeft') || k.has('ShiftRight') ? 2 : 1);
      const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
      const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
      if (k.has('KeyW')) this.pos.addScaledVector(forward, speed);
      if (k.has('KeyS')) this.pos.addScaledVector(forward, -speed);
      if (k.has('KeyD')) this.pos.addScaledVector(right, speed);
      if (k.has('KeyA')) this.pos.addScaledVector(right, -speed);
      if (k.has('KeyE')) this.pos.y += speed;
      if (k.has('KeyQ')) this.pos.y -= speed;
      this.clamp();
    }
    if (this.shakeT > 0) this.shakeT = Math.max(0, this.shakeT - dt);
    this.apply();
  }

  /** A short rumble, used by the biggest card effects. */
  shake(strength: number, duration: number) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.shakeAmp = strength;
    this.shakeDur = this.shakeT = duration;
  }
  private shakeT = 0;
  private shakeDur = 1;
  private shakeAmp = 0;

  private apply() {
    this.camera.position.copy(this.pos);
    if (this.shakeT > 0) {
      const a = this.shakeAmp * (this.shakeT / this.shakeDur);
      this.camera.position.x += (Math.random() - 0.5) * 2 * a;
      this.camera.position.y += (Math.random() - 0.5) * 2 * a;
    }
    this.camera.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
    this.camera.updateMatrixWorld();
  }
}
